<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue'
import Lenis from '@studio-freight/lenis'
import HeaderView from '@/components/HeaderView.vue'
import FooterView from '@/components/FooterView.vue'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import RelatedLinks from '@/components/RelatedLinks.vue'
import DemoModal from '@/components/DemoModal.vue'
import ProductsDemoSection from '@/components/ProductsDemoSection.vue'
import { useSEO } from '@/composables/useSEO'
import { useDemoModal } from '@/composables/useDemoModal'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const { updateMetaTags } = useSEO()
const { isModalOpen, brevoLink, openModal, closeModal } = useDemoModal()

let lenis: Lenis | null = null

onMounted(() => {
  // SEO optimizado para keyword principal
  updateMetaTags({
    title: '¿Qué es un GMAO y por qué es tan valioso para las empresas? | Fourin',
    description:
      'Descubre cómo un GMAO (Gestión de Mantenimiento Asistido por Ordenador) transforma la gestión operativa. Reduce costos hasta 30%, aumenta productividad 25% y optimiza el mantenimiento preventivo y predictivo.',
    keywords:
      'GMAO Latinoamérica, GMAO LATAM, qué es GMAO, software GMAO, sistema mantenimiento LATAM, gestión mantenimiento Latinoamérica, CMMS Latinoamérica',
    url: 'https://fourin.co/gmao-latinoamerica',
    canonical: 'https://fourin.co/gmao-latinoamerica',
    type: 'website',
    organization: true,
    breadcrumbs: [
      { name: 'Inicio', url: '/' },
      { name: 'GMAO Latinoamérica', url: '/gmao-latinoamerica' },
    ],
  })

  lenis = new Lenis({
    lerp: 0.1,
    smoothWheel: true,
  })
  function raf(time: number) {
    lenis?.raf(time)
    requestAnimationFrame(raf)
  }
  requestAnimationFrame(raf)
  window.scrollTo({ top: 0, behavior: 'smooth' })

  // GSAP: Sticky Phone Animation
  const ctx = gsap.context(() => {
    ScrollTrigger.create({
      trigger: '.sticky-feature-section',
      start: 'top top',
      end: 'bottom bottom',
      pin: '.visual-content',
      pinSpacing: false,
    })

    // Animate text blocks on scroll
    gsap.utils.toArray('.feature-block').forEach((block: any) => {
      gsap.from(block, {
        opacity: 0,
        y: 60,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: block,
          start: 'top 85%',
          end: 'top 50%',
          scrub: true,
        },
      })
    })

    // Subtle entrance for the phone
    gsap.from('.phone-mockup', {
      scale: 0.8,
      opacity: 0,
      duration: 1.5,
      ease: 'expo.out',
      scrollTrigger: {
        trigger: '.sticky-feature-section',
        start: 'top 70%',
      },
    })
  })

  // Refresh ScrollTrigger when Lenis updates
  lenis.on('scroll', ScrollTrigger.update)
})

onBeforeUnmount(() => {
  if (lenis) {
    lenis.destroy()
  }
  ScrollTrigger.getAll().forEach((t) => t.kill())
})

const relatedLinks = [
  {
    title: 'Software CMMS en Colombia: Guía Completa 2025',
    url: '/cmms-colombia',
    description: 'Comparativa completa de software CMMS en Colombia',
    type: 'page' as const,
  },
  {
    title: 'Fourintrack - Software CMMS/GMAO',
    url: '/products',
    description: 'Conoce nuestro software GMAO en la nube',
    type: 'product' as const,
  },
  {
    title: 'Blog - Artículos sobre mantenimiento',
    url: '/blog',
    description: 'Aprende sobre mantenimiento y gestión de activos',
    type: 'page' as const,
  },
]
</script>

<template>
  <div>
    <HeaderView />
    <DemoModal :show="isModalOpen" :url="brevoLink" @close="closeModal" />

    <main class="gmao-latam-page">
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="container">
          <Breadcrumbs :items="[
            { name: 'Inicio', url: '/' },
            { name: 'GMAO Latinoamérica', url: '/gmao-latinoamerica' },
          ]" />
          <div class="hero-content">
            <h1 class="hero-title">¿Qué es un GMAO y por qué es tan valioso para las empresas?</h1>
            <p class="hero-description">
              Un GMAO (Gestión de Mantenimiento Asistido por Ordenador) es una plataforma digital
              que centraliza toda la información relacionada con el mantenimiento de los activos de
              una empresa. Descubre cómo transforma la gestión operativa y controla los costos.
            </p>
            <button class="cta-button" @click="openModal">Prueba Gratis 14 Días</button>
          </div>
        </div>
      </section>

      <!-- Content Section -->
      <section class="content-section">
        <div class="container">
          <article class="main-content">
            <p>
              Un <strong>GMAO (Gestión de Mantenimiento Asistido por Ordenador)</strong> es una
              plataforma digital que centraliza toda la información relacionada con el mantenimiento
              de los activos de una empresa. Pero más allá de una simple herramienta organizativa,
              representa un <em>cambio estratégico</em> en cómo las organizaciones gestionan sus
              operaciones y controlan sus costos. Para comprender realmente su atractivo, es
              importante analizar tanto los beneficios económicos documentados como el mecanismo
              interno que hace posible estas mejoras.
            </p>

            <img class="gmao-image" src="../assets/images/gmaoBanner.png" alt="GMAO" />

            <h2>El impacto económico: por qué las empresas invierten en GMAO</h2>
            <p>
              Las razones por las que las empresas adoptan sistemas GMAO están respaldadas por datos
              económicos concretos. Según estudios del
              <strong>Departamento de Energía de Estados Unidos</strong>, el
              <strong>mantenimiento preventivo</strong> (un componente clave de los GMAO) reduce los
              costos operativos entre <strong>12% y 18%</strong> en comparación con el mantenimiento
              reactivo o correctivo. Más impactante aún, cuando se integra
              <strong>mantenimiento predictivo</strong> basado en análisis de datos, los ahorros
              alcanzan entre <strong>25% y 30%</strong>.
              <a href="https://blog.infraspeak.com/es/mantenimiento-preventivo-vs-predictivo/" target="_blank"
                rel="noopener noreferrer" class="source-link">Fuente: Infraspeak</a>
            </p>

            <p>
              A nivel global, las investigaciones de <strong>Deloitte</strong> muestran que el
              mantenimiento predictivo puede
              <strong>aumentar la productividad hasta un 25%</strong>,
              <strong>reducir averías en un 70%</strong> y
              <strong>disminuir costos de mantenimiento en un 25%</strong>. En términos concretos,
              estudios adicionales documentan que las empresas que implementan GMAO logran
              reducciones de costos operativos de hasta un <strong>20%</strong>. Estos no son
              números teóricos: representan <em>reducciones reales</em> en presupuestos de
              mantenimiento que impactan directamente la rentabilidad.
            </p>

            <p>
              Otro aspecto económico importante es la <strong>gestión del inventario</strong>. Los
              sistemas GMAO permiten
              <strong>reducir el valor de las existencias de piezas de repuesto entre 10% y 20%</strong>, evitando tanto
              la compra innecesaria de piezas que nunca se usan como la falta de
              repuestos críticos que podrían detener la producción.
              <a href="https://www.emaint.com/es/benefits-of-cmms/" target="_blank" rel="noopener noreferrer"
                class="source-link">Fuente: eMaint</a>
            </p>

            <p>
              Un caso práctico ilustra la magnitud del impacto: en la industria automotriz, el
              <strong>tiempo de inactividad no planificado</strong> puede costar hasta
              <strong>50.000 dólares por hora</strong>. Un GMAO bien implementado reduce
              drásticamente estos eventos al pasar de una estrategia <em>reactiva</em> a una
              <em>proactiva</em>.
            </p>

            <h2>
              Cómo funciona internamente un GMAO: el sistema de recopilación y procesamiento de
              datos
            </h2>
            <p>
              Para entender realmente qué hace a un GMAO tan valioso, es necesario comprender cómo
              recopila, organiza y utiliza la información. Según
              <a href="https://www.ibm.com/es-es/think/topics/what-is-a-cmms" target="_blank" rel="noopener noreferrer"
                class="source-link">IBM</a>, los sistemas GMAO modernos integran múltiples tecnologías para crear un
              ecosistema
              de gestión inteligente.
            </p>

            <h3>Base de datos centralizada</h3>
            <p>
              El núcleo de cualquier GMAO es una <strong>base de datos centralizada</strong> que
              funciona como <em>repositorio único de verdad</em>. Este sistema almacena información
              sobre todos los activos que requieren mantenimiento: maquinaria, vehículos, equipos,
              infraestructuras. Para cada activo, el GMAO registra:
            </p>
            <ul>
              <li>Especificaciones técnicas y manuales</li>
              <li>Fecha de compra y vida útil estimada</li>
              <li>Historial completo de mantenimiento</li>
              <li>Todas las reparaciones realizadas</li>
              <li>Piezas reemplazadas</li>
              <li>Costo de cada intervención</li>
              <li>Tiempo requerido para cada tarea</li>
            </ul>
            <img class="gmao-image" src="../assets/images/gmao-assets.png" alt="GMAO" />

            <h3>Recopilación de datos desde múltiples fuentes</h3>
            <p>
              Un GMAO moderno no solo registra información introducida manualmente. Recopila datos
              de varias fuentes simultáneamente:
            </p>
            <ul>
              <li>
                <strong>Entrada manual de técnicos:</strong> Los técnicos de mantenimiento registran
                directamente en el sistema (frecuentemente desde aplicaciones móviles) los trabajos
                realizados, tiempos invertidos, materiales utilizados y observaciones técnicas.
              </li>
              <li>
                <strong>Sensores IoT integrados:</strong> Los dispositivos
                <strong>IoT (Internet de las Cosas)</strong> conectados a la maquinaria transmiten
                datos en tiempo real sobre parámetros como temperatura, vibración, presión, humedad,
                consumo de energía y ciclos de funcionamiento. Esta información fluye continuamente
                al GMAO sin intervención humana.
                <a href="https://rosmiman.com/noticias/integracion-de-sistemas-gmao-e-iot-en-fabricas-inteligentes/"
                  target="_blank" rel="noopener noreferrer" class="source-link">Fuente: Rosmiman</a>
              </li>
              <li>
                <strong>Sistemas SCADA y automatización:</strong> En entornos industriales, el GMAO
                se integra con sistemas supervisores que proporcionan datos operativos adicionales.
              </li>
            </ul>

            <h3>Procesamiento y análisis de información</h3>
            <p>Una vez que los datos están centralizados, el GMAO ejecuta análisis sofisticados:</p>
            <ul>
              <li>
                <strong>Detección de patrones:</strong> El sistema identifica si ciertos equipos
                fallan repetidamente en intervalos específicos, si determinadas piezas tienen vida
                útil predecible, o si existen correlaciones entre condiciones operativas y averías.
                Por ejemplo, si un motor siempre presenta problemas después de 5.000 horas de
                funcionamiento, el sistema puede detectar este patrón automáticamente.
              </li>
              <li>
                <strong>Alertas y umbrales:</strong> El GMAO puede configurarse con límites
                específicos. Cuando un sensor reporta que la temperatura de un equipo excede el
                umbral establecido, el sistema genera automáticamente una alerta o incluso inicia
                una orden de trabajo sin intervención manual.
              </li>
              <li>
                <strong>Análisis predictivo:</strong> Los sistemas avanzados aplican algoritmos de
                <strong>inteligencia artificial</strong> para analizar registros históricos y
                predecir cuándo es probable que ocurra una falla. Esto permite programar
                mantenimiento preventivo específicamente cuando es necesario, no en intervalos fijos
                arbitrarios.
                <a href="https://new.abb.com/news/detail/118832/cual-es-la-diferencia-entre-mantenimiento-predictivo-y-mantenimiento-preventivo"
                  target="_blank" rel="noopener noreferrer" class="source-link">Fuente: ABB</a>
              </li>
            </ul>

            <h2>Cómo el GMAO utiliza la información para tomar decisiones</h2>
            <p>Los datos recopilados se transforman en inteligencia operativa de varias formas:</p>

            <h3>Transición de mantenimiento reactivo a proactivo</h3>
            <p>
              Históricamente, muchas empresas operaban con <strong>mantenimiento reactivo</strong>:
              esperaban a que algo se rompiera y entonces lo reparaban. Esto resultaba en costos
              impredecibles, tiempos de inactividad no planificados e inversiones en reparaciones de
              emergencia que son <em>más caras que la prevención</em>.
            </p>

            <p>
              Con un GMAO, la información histórica permite cambiar a
              <strong>mantenimiento preventivo programado</strong>. El sistema automatiza la
              creación de órdenes de trabajo recurrentes basadas en cronogramas preestablecidos o
              ciclos de operación. Pero más importante aún, con datos reales sobre el rendimiento de
              cada activo, estas programaciones se optimizan constantemente.
              <a href="https://blog.infraspeak.com/es/mantenimiento-preventivo-vs-predictivo/" target="_blank"
                rel="noopener noreferrer" class="source-link">Fuente: Infraspeak</a>
            </p>

            <h3>Optimización de recursos de mano de obra</h3>
            <p>
              El GMAO proporciona visibilidad completa sobre cómo se dedica el tiempo de los
              técnicos. Los datos muestran:
            </p>
            <ul>
              <li>Cuánto tiempo realmente toman las tareas comparado con lo estimado</li>
              <li>Qué técnicos son más eficientes</li>
              <li>Si hay cuellos de botella en la asignación de trabajo</li>
              <li>Cuándo se requieren horas extraordinarias</li>
            </ul>

            <p>
              Con esta información, los supervisores pueden planificar mejor, asignar recursos más
              eficientemente y reducir las horas extra innecesarias. Dado que la
              <strong>mano de obra es típicamente el componente más caro del mantenimiento</strong>,
              esta optimización representa <em>ahorros sustanciales</em>.
              <a href="https://mobility-work.com/es/blog/calcular-roi-gmao/" target="_blank" rel="noopener noreferrer"
                class="source-link">Fuente: Mobility Work</a>
            </p>

            <img class="gmao-image" src="../assets/images/gmao-orders.png" alt="GMAO" />

            <h3>Gestión mejorada del inventario</h3>
            <p>
              En lugar de mantener niveles de inventario basados en suposiciones, un GMAO utiliza
              <strong>datos históricos reales</strong> para determinar qué repuestos son necesarios,
              con qué frecuencia se utilizan, y cuándo reabastecer. El sistema puede
              <strong>automatizar órdenes de reabastecimiento</strong> cuando los niveles caen por
              debajo de umbrales específicos, evitando tanto el exceso como la escasez.
              <a href="https://www.mecalux.com.co/blog/gmao" target="_blank" rel="noopener noreferrer"
                class="source-link">Fuente: Mecalux</a>
            </p>

            <h3>Toma de decisiones estratégica</h3>
            <p>
              El GMAO genera <strong>reportes y dashboards personalizados</strong> que transforman
              datos crudos en <em>información accionable</em>. Los indicadores clave de desempeño
              (<strong>KPIs</strong>) que monitorea incluyen:
            </p>
            <ul>
              <li>
                Número de órdenes de trabajo
                <strong>programadas vs. completadas vs. atrasadas</strong>
              </li>
              <li>
                <strong>Eficiencia general del equipo (OEE - Overall Equipment Effectiveness)</strong>
              </li>
              <li><strong>Tiempo promedio de reparación (MTTR)</strong></li>
              <li><strong>Tiempo medio entre fallos (MTBF)</strong></li>
              <li>Análisis de costo por hora de inactividad</li>
              <li>Tendencias de rendimiento a lo largo del tiempo</li>
            </ul>

            <p>
              Estos indicadores permiten que la gerencia identifique problemas emergentes, valide el
              desempeño de estrategias implementadas y haga ajustes informados.
            </p>

            <h3>Cumplimiento normativo y trazabilidad</h3>
            <p>
              Cada acción registrada en un GMAO crea una
              <strong>pista de auditoría completa</strong>. En industrias reguladas (<em>farmacéutica, médica,
                energética, alimentaria</em>), las empresas pueden demostrar que cumplieron con procedimientos de
              mantenimiento
              requeridos. Esto es valioso no solo para reguladores, sino también para garantías de
              equipo y análisis de responsabilidad.
              <a href="https://www.ibm.com/es-es/think/topics/what-is-a-cmms" target="_blank" rel="noopener noreferrer"
                class="source-link">Fuente: IBM</a>
            </p>

            <h2>El ciclo de retroalimentación continuo</h2>
            <p>
              Lo que realmente diferencia a un GMAO efectivo es que crea un ciclo de
              retroalimentación continuo:
            </p>
            <ul>
              <li>Se recopilan datos de operaciones</li>
              <li>Se analizan para identificar patrones y anomalías</li>
              <li>Se toman acciones correctivas o preventivas basadas en estos análisis</li>
              <li>Se registran los resultados en el sistema</li>
              <li>Los nuevos datos mejoran la precisión de los análisis futuro</li>
            </ul>

            <p>
              Esto significa que un GMAO se vuelve <em>más útil con el tiempo</em>. Cuanta más
              información histórica acumula, mejor puede predecir y optimizar. Este enfoque de
              <strong>aprendizaje continuo</strong> es lo que diferencia a los sistemas GMAO
              avanzados de las soluciones básicas de gestión.
              <a href="https://www.yuman.io/es/blog-post/148-como-se-mide-el-impacto-de-un-proyecto-de-gmao/"
                target="_blank" rel="noopener noreferrer" class="source-link">Fuente: Yuman</a>
            </p>

            <h2>Perspectiva de mercado</h2>
            <p>
              El atractivo de los GMAO está confirmado por el crecimiento del mercado. El mercado
              global de <strong>mantenimiento predictivo</strong> (un componente clave de los GMAO
              modernos) se valoró en <strong>5.860 millones de dólares en 2025</strong> y se
              proyecta que alcance <strong>40.080 millones de dólares en 2035</strong>, con una tasa
              de crecimiento anual compuesta del <strong>21,2%</strong>. Este crecimiento refleja
              que las empresas reconocen y validan continuamente el
              <strong>retorno sobre la inversión (ROI)</strong> que ofrecen estas plataformas.
              <a href="https://www.yuman.io/es/blog-post/148-como-se-mide-el-impacto-de-un-proyecto-de-gmao/"
                target="_blank" rel="noopener noreferrer" class="source-link">Fuente: Yuman</a>
            </p>

            <img class="gmao-image" src="../assets/images/tecnicos.png" alt="GMAO" />

            <h2>Conclusión</h2>
            <p>
              Los GMAO son atractivos para las empresas porque representan una
              <strong>transformación fundamental</strong> en la gestión operativa: cambian el
              mantenimiento de ser una función <em>reactiva y costosa</em> a ser una actividad
              <em>estratégica y optimizada</em>. Logran esto centralizando datos de múltiples
              fuentes, analizándolos continuamente para identificar patrones, y proporcionando
              inteligencia accionable que
              <strong>reduce costos, aumenta la disponibilidad de equipos y mejora la toma de
                decisiones</strong>. Para empresas que manejan activos complejos y costosos, este cambio de paradigma
              justifica ampliamente la inversión en un GMAO.
              <a href="https://ibernova.com/blog/gmao-que-es-y-como-mejora-la-gestion-de-mantenimiento/" target="_blank"
                rel="noopener noreferrer" class="source-link">Fuente: Ibernova</a>
            </p>
          </article>
        </div>
      </section>

      <!-- New Sticky Mobile Section -->
      <section class="sticky-feature-section">
        <div class="container">
          <div class="sticky-grid">
            <div class="text-content">
              <div class="feature-block first-block">
                <div class="icon-group">
                  <div class="icon-item blue">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                      stroke-linecap="round" stroke-linejoin="round">
                      <path
                        d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1-1.73z">
                      </path>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                      <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    </svg>
                  </div>
                  <div class="icon-item green">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                      stroke-linecap="round" stroke-linejoin="round">
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                      <path d="m9 14 2 2 4-4"></path>
                    </svg>
                  </div>
                  <div class="icon-item orange">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                      stroke-linecap="round" stroke-linejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                </div>
                <h2 class="sticky-title">Controla tu <span class="highlight">operación</span> a donde quiera que vayas
                </h2>
                <p class="sticky-description">
                  Diseñado para operaciones exigentes. Centraliza flujos de trabajo, monitoriza
                  dispositivos IoT en tiempo real y toma decisiones basadas en datos puros.
                </p>
              </div>

              <div class="feature-block">
                <h3 class="sticky-subtitle">Monitorización remota</h3>
                <p class="sticky-description">
                  Mantén el control total de tu planta o activos desde cualquier lugar del mundo.
                  Recibe notificaciones push instantáneas cuando algo requiere tu atención.
                </p>
              </div>

              <div class="feature-block">
                <h3 class="sticky-subtitle">Órdenes de trabajo digitales</h3>
                <p class="sticky-description">
                  Elimina el papel de tu operación. Tus técnicos pueden recibir, ejecutar y cerrar
                  órdenes de trabajo directamente desde la aplicación móvil.
                </p>
              </div>

              <div class="feature-block">
                <h3 class="sticky-subtitle">Toma de decisiones ágil</h3>
                <p class="sticky-description">
                  Con datos en tiempo real, tu equipo puede reaccionar ante imprevistos antes de que
                  se conviertan en fallas costosas.
                </p>
              </div>
            </div>

            <div class="visual-content">
              <div class="sticky-element">
                <img src="../assets/mockups/mockup3.webp" alt="App Mockup" class="phone-mockup" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Demo Section -->
      <ProductsDemoSection />

      <!-- Related Links -->
      <section class="related-section">
        <div class="container">
          <RelatedLinks :links="relatedLinks" title="Contenido Relacionado" />
        </div>
      </section>
    </main>

    <FooterView />
  </div>
</template>

<style scoped>
main {
  margin-top: 90px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-section {
  padding: 100px 0 80px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.hero-content {
  max-width: 800px;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  line-height: 1.2;
}

.hero-description {
  font-size: 1.25rem;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 32px;
}

.cta-button {
  padding: 16px 32px;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(18, 151, 239, 0.3);
}

.content-section {
  padding: 80px 0;
  background: white;
}

.main-content {
  max-width: 1200px;
  margin: 0 auto;
}

.main-content h2 {
  font-size: 2rem;
  font-weight: 600;
  color: #1e293b;
  margin: 60px 0 24px;
  line-height: 1.3;
}

.main-content h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 40px 0 20px;
}

.main-content p {
  font-size: 1.125rem;
  line-height: 1.8;
  color: #374151;
  margin-bottom: 24px;
}

.gmao-image {
  width: 100%;
  max-width: 100%;
  border-radius: 25px;
  border: 4px solid #e4e9f1;
  margin: 40px 0;
}

.main-content ul {
  margin: 24px 0;
  padding-left: 24px;
}

.main-content li {
  font-size: 1.125rem;
  line-height: 1.8;
  color: #374151;
  margin-bottom: 12px;
}

.main-content strong {
  font-weight: 600;
  color: #1e293b;
}

.main-content em {
  font-style: italic;
  color: #475569;
}

.source-link {
  display: inline-block;
  margin-left: 8px;
  font-size: 0.875rem;
  color: #1297ef;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.source-link:hover {
  color: #0d5fef;
  text-decoration: underline;
}

.source-link::before {
  content: '🔗';
  margin-right: 4px;
  font-size: 0.75rem;
}

.related-section {
  padding: 30px 0;
}

.references-section {
  margin: 80px 0 60px;
  padding: 40px;
  background: #f8fafc;
  border-radius: 16px;
  border-left: 4px solid #1297ef;
}

.references-section h2 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
}

.references-section>p {
  color: #64748b;
  margin-bottom: 24px;
  font-size: 1rem;
}

.references-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.references-list li {
  margin-bottom: 12px;
  padding-left: 24px;
  position: relative;
}

.references-list li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: #1297ef;
  font-weight: 600;
}

.references-list a {
  color: #1297ef;
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.3s ease;
  display: inline-block;
}

.references-list a:hover {
  color: #0d5fef;
  text-decoration: underline;
  transform: translateX(4px);
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-description {
    font-size: 1.125rem;
  }

  .main-content h2 {
    font-size: 1.5rem;
  }

  .main-content p,
  .main-content li {
    font-size: 1rem;
  }
}

/* Sticky Feature Section Styles */
.sticky-feature-section {
  padding: 120px 0;
  background-color: #ffffff;
  position: relative;
}

.sticky-grid {
  display: flex;
  gap: 80px;
  position: relative;
}

.text-content {
  flex: 1.2;
  display: flex;
  flex-direction: column;
  gap: 250px;
  padding-bottom: 200px;
}

.visual-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100%;
}

.sticky-element {
  position: sticky;
  top: 15vh;
  z-index: 10;
  width: 100%;
  display: flex;
  justify-content: center;
}

.feature-block {
  max-width: 550px;
}

.icon-group {
  display: flex;
  margin-bottom: 30px;
}

.icon-item {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border: 4px solid #fff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  margin-left: -15px;
}

.icon-item:first-child {
  margin-left: 0;
}

.icon-item.blue {
  background-color: #1297ef;
  z-index: 3;
}

.icon-item.green {
  background-color: #10b981;
  z-index: 2;
}

.icon-item.orange {
  background-color: #f59e0b;
  z-index: 1;
}

.sticky-title {
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.1;
  color: #1e293b;
  margin-bottom: 24px;
  letter-spacing: -0.02em;
}

.highlight {
  color: #1297ef;
}

.sticky-subtitle {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 20px;
}

.sticky-description {
  font-size: 1.35rem;
  line-height: 1.6;
  color: #64748b;
}

.phone-mockup {
  width: 100%;
  max-width: 480px;
  height: auto;
  filter: drop-shadow(0 40px 80px rgba(0, 0, 0, 0.2));
}

@media (max-width: 1024px) {
  .sticky-grid {
    flex-direction: column-reverse;
    gap: 60px;
  }

  .text-content {
    gap: 100px;
    padding-bottom: 50px;
  }

  .sticky-element {
    position: relative;
    top: 0;
  }

  .sticky-title {
    font-size: 2.8rem;
  }

  .visual-content {
    min-height: auto;
  }
}

@media (max-width: 768px) {
  .sticky-title {
    font-size: 2.2rem;
  }

  .sticky-subtitle {
    font-size: 1.8rem;
  }

  .sticky-description {
    font-size: 1.1rem;
  }

  .icon-item {
    width: 44px;
    height: 44px;
  }
}
</style>
